import type { WorkspaceChatMessage, WorkspaceTable } from '../services/workspace-assistant.types';

/** Assistant replies and their tables as files (CSV for spreadsheets, Markdown for documents) and clipboard text. Saving: saveTextFile.ts. */

type Grid = { header: string[]; rows: Array<Array<string | number | boolean | null | undefined>> };

export const tableGrid = (table: Pick<WorkspaceTable, 'columns' | 'rows'>, format = (v: any) => v): Grid => ({
  header: table.columns.map(c => c.label),
  rows: table.rows.map(r => table.columns.map(c => format(r.cells[c.key], c))),
});

const csvCell = (v: unknown) => {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
/** UTF-8 with a byte-order mark: Excel then opens Arabic text correctly. */
export const toCsv = ({ header, rows }: Grid) => '\uFEFF' + [header, ...rows].map(r => r.map(csvCell).join(',')).join('\r\n');
/** Tab-separated: pastes into Excel / Google Sheets as cells. */
export const toTsv = ({ header, rows }: Grid) => [header, ...rows].map(r => r.map(v => (v == null ? '' : String(v).replace(/[\t\n\r]+/g, ' '))).join('\t')).join('\n');
const mdCell = (v: unknown) => (v == null ? '' : String(v).replace(/\|/g, '\\|').replace(/[\n\r]+/g, ' '));
export const toMarkdownTable = ({ header, rows }: Grid) => [`| ${header.map(mdCell).join(' | ')} |`, `|${header.map(() => '---').join('|')}|`, ...rows.map(r => `| ${r.map(mdCell).join(' | ')} |`)].join('\n');

/** One reply (text + its tables) as a Markdown document. */
export function messageMarkdown(message: WorkspaceChatMessage, format?: (v: any, column: any) => any) {
  const parts = [message.content.trim()];
  for (const t of message.tables || []) parts.push(`### ${t.title}${t.total != null ? ` (${t.total})` : ''}`, toMarkdownTable(tableGrid(t, format)));
  return parts.filter(Boolean).join('\n\n');
}

/** The whole chat as a Markdown document: questions as headings, replies with their tables. */
export function chatMarkdown(messages: WorkspaceChatMessage[], title: string, format?: (v: any, column: any) => any) {
  const out = [`# ${title}`];
  for (const m of messages) out.push(m.role === 'user' ? `## ${m.content.trim()}` : messageMarkdown(m, format));
  return out.join('\n\n') + '\n';
}

export const exportFileName = (base: string, extension: string) => {
  const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
  const safe = base.normalize('NFKC').replace(/[\\/:*?"<>|\s]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'assistant';
  return `${safe}-${stamp}.${extension}`;
};

/** A display value: dates, waiting hours (days past 48 h), numbers in the reader's locale. */
export function formatWorkspaceCell(value: any, kind: string | undefined, locale: string) {
  if (value == null || value === '') return '';
  const ar = /^ar/.test(locale);
  if (kind === 'hours' && typeof value === 'number') {
    if (value < 1) return ar ? 'أقل من ساعة' : '< 1 h';
    if (value < 48) return ar ? `${Math.round(value)} ساعة` : `${Math.round(value)} h`;
    const days = Math.round(value / 24);
    return ar ? `${days} يوم` : `${days} ${days === 1 ? 'day' : 'days'}`;
  }
  if (kind === 'date' && typeof value === 'string' && /^\d{4}-\d\d-\d\d/.test(value)) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    try {
      const timed = !/T00:00:00(\.0+)?Z$/.test(value) && value.length > 10;
      return d.toLocaleString(locale, { day: 'numeric', month: 'short', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}), ...(timed ? { hour: '2-digit', minute: '2-digit' } : {}) });
    } catch { return value.slice(0, 16).replace('T', ' '); }
  }
  if ((kind === 'number' || kind === 'money') && typeof value === 'number') {
    try { return value.toLocaleString(locale, { maximumFractionDigits: 2 }); } catch { return String(value); }
  }
  if (typeof value === 'boolean') return ar ? (value ? 'نعم' : 'لا') : value ? 'Yes' : 'No';
  return String(value);
}

/** Status words → a colour: done / ok, attention, failed. */
export function statusTone(value: unknown): 'good' | 'warn' | 'bad' | 'neutral' {
  const s = String(value ?? '').toLowerCase();
  if (/^(read|delivered|approved|completed|active|on|paid|published|done|in_stock|available|مفعل)$/.test(s)) return 'good';
  if (/^(failed|rejected|cancell?ed|refunded|off|missed|error|out_of_stock|unavailable|paused|متوقف)$/.test(s)) return 'bad';
  if (/^(pending|scheduled|sent|in_progress|processing|draft|partially_paid|low_stock|rescheduled)$/.test(s)) return 'warn';
  return 'neutral';
}

/**
 * Splits text where it names a record the reply links to (names of rows the tools returned), so
 * those names can be tapped. Longest names first; names shorter than 3 letters never link.
 */
export function linkSegments(text: string, labels: string[]): Array<{ text: string; link?: number }> {
  const usable = labels.map((label, index) => ({ label: label.trim(), index })).filter(l => l.label.length >= 3).sort((a, b) => b.label.length - a.label.length).slice(0, 80);
  if (!usable.length || !text) return [{ text }];
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(usable.map(l => escape(l.label)).join('|'), 'g');
  const out: Array<{ text: string; link?: number }> = [];
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    const at = match.index!, before = text[at - 1], after = text[at + match[0].length];
    // Whole words only: "Ali" must not link inside "Alia".
    if (before && /[\p{L}\p{N}]/u.test(before) || after && /[\p{L}\p{N}]/u.test(after)) continue;
    if (at > cursor) out.push({ text: text.slice(cursor, at) });
    out.push({ text: match[0], link: usable.find(l => l.label === match[0])!.index });
    cursor = at + match[0].length;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor) });
  return out.length ? out : [{ text }];
}
